import { useMemo } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import { getInternalTransferRoleLabelKey } from "../../../../../Services/baasInternalTransfersApi";
import {
  isBaasExitRailVirtualAccount,
  isBaasOtcVirtualAccount,
  isBaasSegregationWalletAccount,
  isVirtualInstanceShellAccount,
} from "../../../../../Services/baasVirtualShells";
import {
  getAdditionalCurrencyCount,
  getPrimaryBalance,
  formatBaasAvailableWithLimit,
} from "../../../../../Services/baasAccountsListApi";
import { formatMonetaryValue } from "../../../../../Utils/formatters";
import { buildBaasAccountDetailPath } from "../../constants";

const OPERATION_SHELL_ROLES = new Set([
  "operating",
  "overdraft_reserve",
  "program_reserve",
  "reserve",
]);

/** Display order for Shells operacionais cards (Root first). */
const OPERATION_SHELL_ROLE_ORDER = {
  root: 0,
  operating: 1,
  overdraft_reserve: 2,
  overdraft: 2,
  program_reserve: 3,
  program: 3,
  reserve: 5,
};

const formatBalanceSummary = (balances = []) => {
  const primary = getPrimaryBalance(balances);
  if (!primary) return intl.get("baas.accounts.noBalances");

  const primaryLabel = formatBaasAvailableWithLimit(primary);
  const extraCount = getAdditionalCurrencyCount(balances);
  if (extraCount === 0) return primaryLabel;

  return `${primaryLabel} · ${intl.get("baas.accounts.additionalCurrencies", {
    count: extraCount,
  })}`;
};

/** Prefer explicit transfer-options `role`; fall back to UI type only for pin labels. */
const resolveRole = (account) =>
  String(account?.role || account?.type || "")
    .trim()
    .toLowerCase();

/**
 * Instance Root pin from internal-transfer-options (`role=root` / RootAccountId).
 * Prefer sourceAccounts — that is the program hierarchy root (e.g. VALUT GLOBAL LLC),
 * not leftover ENTITY "ROOT" litter (those lack transfer-options root role unless pinned).
 *
 * @param {object|null|undefined} account
 * @param {{ rootAccountId?: string|null }} [opts] optional instance RootAccountId match
 */
export const isContasInstanceRootAccount = (account, { rootAccountId } = {}) => {
  if (!account) return false;
  if (isBaasSegregationWalletAccount(account)) return false;
  const rootId = String(rootAccountId || "").trim();
  // Peer Conta Digital on matriz Contas: treat as this pin's Root only when the
  // selected tenant's RootAccountId matches (after selectInstanceForPeerOps).
  if (account?.isInstanceRoot === true || account?.is_instance_root === true) {
    return Boolean(rootId && rootId === String(account.id || "").trim());
  }
  // Explicit transfer-options role wins — do not treat every type=root list row as pin.
  if (String(account?.role || "").trim().toLowerCase() === "root") return true;
  if (account.is_root === true || account.isRoot === true) return true;
  if (rootId && rootId === String(account.id || "").trim()) return true;
  return false;
};

/**
 * Instance operation shells for cards: Root pin + OPERATING / OVERDRAFT / PROGRAM.
 * Uses explicit `role` from transfer-options when present; never promotes a normal
 * Contas list row to a shell just because UI `type` mapped to "operational".
 */
export const isContasOperationShellAccount = (account) => {
  if (!account) return false;
  if (isBaasExitRailVirtualAccount(account)) return false;
  // Matrix peer Conta Digital (GET peer-roots) lives on Contas as ENTITY | CONTA — not INFRA shells.
  if (account?.isInstanceRoot === true || account?.is_instance_root === true) {
    return false;
  }
  const explicitRole = String(account?.role || "")
    .trim()
    .toLowerCase();
  if (explicitRole === "root") return true;
  if (OPERATION_SHELL_ROLES.has(explicitRole)) return true;
  // ENTITY setup litter (name OPERATING / …) — not program-owner Root.
  if (isVirtualInstanceShellAccount(account)) return true;
  return false;
};

/** Exit-rail virtuals (WALLET + OTC) for Contas virtuais section. */
export const isContasVirtualWalletAccount = (account) =>
  Boolean(account) && isBaasExitRailVirtualAccount(account);

/** @deprecated Prefer isContasOperationShellAccount / isContasVirtualWalletAccount */
export const isContasVirtualSectionAccount = (account) =>
  isContasOperationShellAccount(account) || isContasVirtualWalletAccount(account);

const cardLabel = (account) => {
  const roleKey = getInternalTransferRoleLabelKey(account?.role);
  if (roleKey) return intl.get(roleKey);
  // OTC is internal — display as WALLET (same as segregation wallet).
  if (isBaasOtcVirtualAccount(account) || isBaasSegregationWalletAccount(account)) {
    return intl.get("baas.accounts.type.wallet") || "WALLET";
  }
  const type = String(account?.type || "").trim();
  if (type) {
    const key = `baas.accounts.type.${type}`;
    const label = intl.get(key);
    if (label && label !== key) return label;
  }
  return account?.role || account?.type || intl.get("baas.accounts.type.virtual");
};

const displayName = (account) =>
  account?.name
  || account?.entityName
  || account?.alias
  || account?.id;

const i18nPrefixForVariant = (variant) =>
  variant === "operation"
    ? "baas.accounts.operationShellsSection"
    : "baas.accounts.virtualSection";

/**
 * Contas cards panel — operation shells or exit-rail virtuals.
 * variant "operation": Root + OPERATING / OVERDRAFT / PROGRAM
 * variant "virtual": WALLET + OTC (saída Root)
 */
export const BaasEntityVirtualAccountsPanel = ({
  accounts = [],
  shellAccounts = [],
  variant = "virtual",
  loading = false,
  error = null,
  onCreateVirtualClick = null,
  /** When set with onSelectAccount, cards select in-place instead of navigating to Contas detail. */
  selectedAccountId = null,
  onSelectAccount = null,
}) => {
  const history = useHistory();
  const i18nPrefix = i18nPrefixForVariant(variant);
  const selectMode = typeof onSelectAccount === "function";
  const rows = useMemo(() => {
    const matches =
      variant === "operation" ? isContasOperationShellAccount : isContasVirtualWalletAccount;
    const byId = new Map();
    for (const account of [...(shellAccounts || []), ...(accounts || [])]) {
      if (!matches(account)) continue;
      const id = String(account?.id || "").trim();
      if (!id) continue;
      if (!byId.has(id)) byId.set(id, account);
    }
    const list = Array.from(byId.values());
    if (variant !== "operation") return list;
    return list.sort((a, b) => {
      const aOrder = OPERATION_SHELL_ROLE_ORDER[resolveRole(a)] ?? 50;
      const bOrder = OPERATION_SHELL_ROLE_ORDER[resolveRole(b)] ?? 50;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.name || a.alias || a.id).localeCompare(
        String(b.name || b.alias || b.id),
      );
    });
  }, [accounts, shellAccounts, variant]);

  const errorMessage = error
    ? (() => {
        const code = String(error?.code || "").toLowerCase();
        if (code === "api_key_not_found") {
          return (
            intl.get(`${i18nPrefix}.apiKeyMissing`)
            || error?.detail
            || error?.message
            || intl.get(`${i18nPrefix}.loadFailed`)
          );
        }
        if (code === "invalid_api_key" || code === "baas_unauthorized") {
          return (
            intl.get(`${i18nPrefix}.apiKeyInvalid`)
            || error?.detail
            || error?.message
            || intl.get(`${i18nPrefix}.loadFailed`)
          );
        }
        return error?.detail || error?.message || intl.get(`${i18nPrefix}.loadFailed`);
      })()
    : null;

  const isOperation = variant === "operation";
  /** Shells: 4 cards (or 5 with OTC) on one xl row; wallets keep 3-col. */
  const gridClass = isOperation
    ? rows.length >= 5
      ? "grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-5"
      : "grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4"
    : "grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3";

  return (
    <section className="min-w-0 w-full space-y-1.5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="dashboard-section-label mb-0.5">{intl.get(`${i18nPrefix}.section`)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get(`${i18nPrefix}.subtitle`)}
          </p>
        </div>
        {!isOperation && typeof onCreateVirtualClick === "function" ? (
          <button
            type="button"
            className="btn-primary w-auto shrink-0 inline-flex items-center gap-2 !text-xs"
            onClick={onCreateVirtualClick}
          >
            {intl.get("baas.accounts.virtualCreate.submit")}
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="app-card px-3 py-4 text-xs text-gray-500 dark:text-gray-400">
          {intl.get(`${i18nPrefix}.loading`)}
        </div>
      ) : null}

      {!loading && errorMessage ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {errorMessage}
        </div>
      ) : null}

      {!loading && rows.length === 0 && !errorMessage ? (
        <div className="app-card px-3 py-4 text-xs text-gray-500 dark:text-gray-400">
          {intl.get(`${i18nPrefix}.empty`)}
        </div>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div className={gridClass}>
          {rows.map((account) => {
            const isSelected =
              selectMode
              && String(selectedAccountId || "").trim() === String(account.id || "").trim();
            return (
              <button
                key={account.id}
                type="button"
                aria-pressed={selectMode ? isSelected : undefined}
                className={`summary-card flex min-w-0 w-full flex-col !bg-white !p-2.5 text-left transition hover:border-[#e7a73f]/50 dark:!border-brand-dark-border dark:!bg-brand-dark-elevated ${
                  isSelected
                    ? "!border-[#e7a73f] ring-1 ring-[#e7a73f]/40 dark:!border-brand-gold-mid"
                    : ""
                }`}
                onClick={() => {
                  if (selectMode) {
                    onSelectAccount(account);
                    return;
                  }
                  history.push(buildBaasAccountDetailPath(account.id));
                }}
              >
                <div className="min-w-0 overflow-hidden">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-[#a66f1d] dark:text-brand-gold-mid">
                    {cardLabel(account)}
                  </p>
                  <p className="mt-0.5 truncate text-xs font-semibold text-brand-dark dark:text-gray-100">
                    {displayName(account)}
                  </p>
                  <p
                    className="truncate font-mono text-[10px] leading-tight text-gray-500 dark:text-gray-400"
                    title={account.id}
                  >
                    {account.id}
                  </p>
                </div>

                <div className="mt-2 border-t border-gray-100 pt-2 dark:border-brand-dark-border">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {intl.get("baas.accounts.columns.balances")}
                  </p>
                  <p
                    className="mt-0.5 truncate text-xs font-semibold tabular-nums text-brand-dark dark:text-gray-100"
                    title={formatBalanceSummary(account.balances)}
                  >
                    {formatBalanceSummary(account.balances)}
                  </p>
                </div>

                <span className="mt-2 text-[10px] font-semibold text-[#a66f1d] dark:text-brand-gold-mid">
                  {selectMode
                    ? isSelected
                      ? intl.get("baas.root.shellPicker.selected")
                      : intl.get("baas.root.shellPicker.select")
                    : `${intl.get("baas.accounts.detailAction")} →`}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

export default BaasEntityVirtualAccountsPanel;
